import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { CanMatchFn, GuardResult, MaybeAsync, Router, Routes } from '@angular/router';
import { AUTHENTICATION_SERVICE } from '@processpuzzle/auth/domain';
import { publishedAppRoutesGuard } from '@processpuzzle/base-app';
import { firstValueFrom, isObservable } from 'rxjs';
import { currentApplicationId } from '../navigation/current-application';

/**
 * Logs the visitor in, then registers the organization's published application as the runtime host's
 * children — `fallback` while it has never been published.
 *
 * **One guard, the login first, rather than `loginRequiredGuard` beside base-app's guard.** Filling in
 * children has to happen in `canMatch`, which the router runs while it is still recognizing the URL —
 * before any `canActivate` — and it runs a route's `canMatch` guards concurrently. Either way the
 * published read would go out before there is a session, and be refused. So this does what
 * `loginRequiredGuard` does — the requested URL goes to the identity provider directly — and only
 * then hands over.
 */
export function publishedApplicationGuard(fallback: Routes): CanMatchFn {
  const fillChildren = publishedAppRoutesGuard(currentApplicationId, fallback);

  return async (route, segments, currentSnapshot) => {
    const authService = inject(AUTHENTICATION_SERVICE);
    const router = inject(Router);
    // Captured before the first await, which ends the injection context base-app's guard needs.
    const injector = inject(EnvironmentInjector);

    if (!(await authService.authenticate())) {
      const requested = router.currentNavigation()?.extractedUrl.toString() ?? `/${segments.map((segment) => segment.path).join('/')}`;
      await authService.login(requested);
      if (!authService.isAuthenticated()) return router.createUrlTree(['/auth/login']);
    }

    const result: MaybeAsync<GuardResult> = runInInjectionContext(injector, () => fillChildren(route, segments, currentSnapshot));
    return isObservable(result) ? firstValueFrom(result) : result;
  };
}
